/**
 * Lightness against chromaticity, for judging whites and blacks.
 *
 * A different question from MyScatterChart's hue plot: a good white or black is
 * one that is light (or dark) *and* as close to neutral as possible, so the
 * axes here are L* against chromaticity rather than a* against b*. The tooltip
 * adds the cluster width, because a candidate that only reaches its colour at
 * an unusable width is not a candidate.
 */
import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Box from '@mui/material/Box';

const defaultMargin = { top: 12, right: 16, left: 12, bottom: 28 };

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <Box
      sx={{
        p: 1,
        bgcolor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 1,
        boxShadow: 1,
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <div>L* : {Number(d.l_star).toFixed(4)}</div>
      <div>Chromaticity : {Number(d.chromaticity).toFixed(4)}</div>
      {d.cluster_width != null && (
        <div>Cluster width : {Number(d.cluster_width).toFixed(4)}</div>
      )}
    </Box>
  );
};

// ``domainData``: the points the axes must cover (the whole exploration), so
// they stay put while a slider or scope switch changes ``data``.
const MyScatterChartWhiteBlack = ({ data, domainData, width = 520, height = 380, margin = defaultMargin }) => {
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          typography: 'body2',
        }}
      >
        No samples to display
      </Box>
    );
  }

  const extent = Array.isArray(domainData) && domainData.length ? domainData : data;
  const minLStar = Math.min(...extent.map(entry => entry.l_star));
  const maxLStar = Math.max(...extent.map(entry => entry.l_star));
  const minChromaticity = Math.min(...extent.map(entry => entry.chromaticity));
  const maxChromaticity = Math.max(...extent.map(entry => entry.chromaticity));

  const xDomain = [Math.floor(minLStar), Math.ceil(maxLStar)];
  const yDomain = [Math.floor(minChromaticity), Math.ceil(maxChromaticity)];

  return (
    <Box sx={{ width, flexShrink: 0, mx: 'auto' }}>
      <ScatterChart width={width} height={height} margin={margin}>
        <XAxis
          type="number"
          domain={xDomain}
          allowDataOverflow
          dataKey="l_star"
          name="L*"
          tick={{ fontSize: 11 }}
          stroke="#94a3b8"
          label={{ value: 'L*', angle: 0, position: 'insideBottom', offset: -12, fill: '#64748b', fontSize: 12 }}
        />
        <YAxis
          type="number"
          domain={yDomain}
          allowDataOverflow
          dataKey="chromaticity"
          name="Chromaticity"
          tick={{ fontSize: 11 }}
          stroke="#94a3b8"
          label={{ value: 'Chromaticity', angle: -90, position: 'insideLeft', offset: 0, fill: '#64748b', fontSize: 12 }}
        />
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
        <Scatter name="L* vs Chromaticity" data={data} fill="#3b82f6" fillOpacity={0.75} />
      </ScatterChart>
    </Box>
  );
};

export default MyScatterChartWhiteBlack;
